import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { loginWithGoogle } from '../store/thunk/authThunk';
import { addToCart, fetchCartInfo } from '../store/thunk/cartThunk';
import { clearLocalCart } from '../store/reducer/cartSlice';
import { getSecureItem, removeSecureItem } from '../utils/secureStorage';

// Google OAuth configuration and service
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || process.env.REACT_APP_GOOGLE_ID;

// Load Google Identity Services script
const loadGoogleScript = () => {
    return new Promise((resolve, reject) => {
        if (window.google) {
            resolve(window.google);
            return;
        }

        const script = document.createElement('script');
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;
        script.onload = () => {
            if (window.google) {
                resolve(window.google);
            } else {
                reject(new Error('Google Identity Services failed to load'));
            }
        };
        script.onerror = () => reject(new Error('Failed to load Google Identity Services'));
        document.head.appendChild(script);
    });
};

// Initialize Google OAuth
const initializeGoogleAuth = async () => {
    try {
        const google = await loadGoogleScript();

        if (!GOOGLE_CLIENT_ID) {
            throw new Error('Google Client ID is not configured');
        }

        return google;
    } catch (error) {
        console.error('Failed to initialize Google Auth:', error);
        throw error;
    }
};

// Google Sign-In function
const signInWithGoogle = () => {
    return new Promise(async (resolve, reject) => {
        try {
            const google = await initializeGoogleAuth();

            // Initialize Google Identity Services
            google.accounts.id.initialize({
                client_id: GOOGLE_CLIENT_ID,
                callback: (response) => {
                    if (response.credential) {
                        resolve({
                            credential: response.credential,
                            clientId: response.clientId,
                        });
                    } else {
                        reject(new Error('No credential received from Google'));
                    }
                },
                auto_select: false,
                cancel_on_tap_outside: true,
            });

            // Trigger the sign-in prompt
            google.accounts.id.prompt((notification) => {
                if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
                    // Fallback to popup if prompt is not displayed
                    google.accounts.id.renderButton(
                        document.createElement('div'),
                        {
                            type: 'standard',
                            theme: 'outline',
                            size: 'large',
                            text: 'signin_with',
                            shape: 'pill',
                        }
                    );

                    // If prompt fails, try popup method
                    const popup = window.open(
                        `https://accounts.google.com/oauth/authorize?client_id=${GOOGLE_CLIENT_ID}&redirect_uri=${encodeURIComponent(window.location.origin)}&response_type=code&scope=openid email profile`,
                        'Google Login',
                        'width=500,height=600,scrollbars=yes,resizable=yes'
                    );

                    const checkClosed = setInterval(() => {
                        if (popup.closed) {
                            clearInterval(checkClosed);
                            reject(new Error('Popup was closed by user'));
                        }
                    }, 1000);
                }
            });

        } catch (error) {
            reject(error);
        }
    });
};

// Alternative Google Sign-In using popup method
const signInWithGooglePopup = () => {
    return new Promise(async (resolve, reject) => {
        try {
            const google = await initializeGoogleAuth();

            // Create a temporary div for the button
            const tempDiv = document.createElement('div');
            tempDiv.style.display = 'none';
            document.body.appendChild(tempDiv);

            try {
                google.accounts.id.initialize({
                    client_id: GOOGLE_CLIENT_ID,
                    callback: (response) => {
                        document.body.removeChild(tempDiv);
                        if (response.credential) {
                            resolve({
                                credential: response.credential,
                                clientId: response.clientId,
                            });
                        } else {
                            reject(new Error('No credential received from Google'));
                        }
                    },
                    auto_select: false,
                    cancel_on_tap_outside: true,
                });

            } catch (initError) {
                console.error('❌ Google ID initialization failed:', initError);
                document.body.removeChild(tempDiv);
                reject(new Error(`Google initialization failed: ${initError.message}`));
                return;
            }

            // Render button and trigger click
            google.accounts.id.renderButton(tempDiv, {
                type: 'standard',
                theme: 'outline',
                size: 'large',
                text: 'signin_with',
                shape: 'pill',
            });

            // Trigger the button click programmatically
            setTimeout(() => {
                const button = tempDiv.querySelector('[role="button"]');
                if (button) {
                    button.click();
                } else {
                    console.error('Failed to find Google sign-in button');
                    document.body.removeChild(tempDiv);
                    reject(new Error('Failed to render Google sign-in button'));
                }
            }, 100);

        } catch (error) {
            reject(error);
        }
    });
};

// Decode JWT token to get user info
const decodeJWT = (token) => {
    try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
            atob(base64)
                .split('')
                .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join('')
        );
        const decoded = JSON.parse(jsonPayload);

        return decoded;
    } catch (error) {
        console.error('Failed to decode JWT:', error);
        return null;
    }
};

export {
    initializeGoogleAuth,
    signInWithGoogle,
    signInWithGooglePopup,
    decodeJWT,
    GOOGLE_CLIENT_ID,
};

// Helper function to get device information
const getDeviceInformation = () => {
    try {
        const userAgent = navigator.userAgent;
        const platform = navigator.platform;
        const vendor = navigator.vendor;

        return JSON.stringify({
            userAgent,
            platform,
            vendor,
            language: navigator.language,
            screenResolution: `${window.screen.width}x${window.screen.height}`,
        });
    } catch (error) {
        return '';
    }
};

// Helper function to sync local cart with user account
const syncLocalCartToAccount = async (dispatch) => {
    try {
        const localCart = getSecureItem("localCart");


        if (localCart?.item_info && Object.keys(localCart.item_info).length > 0) {

            const addCartPromises = Object.values(localCart.item_info).map((item) => {
                const payload = {
                    product_id: item.product_id,
                    product_quantity: item.product_quantity,
                    preferences: item.product_preference || item.preferences,
                };
                return dispatch(addToCart(payload)).unwrap();
            });

            await Promise.all(addCartPromises);

            // Clear local cart after successful sync
            removeSecureItem("localCart");
            dispatch(clearLocalCart());

            return true;
        }

        return false;
    } catch (error) {
        console.error("Error syncing local cart:", error);
        // Do not throw error - login should still succeed even if cart sync fails
        return false;
    }
};

// Custom hook for Google login - COMMON REUSABLE HOOK
export const useGoogleLogin = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const handleGoogleLogin = async (from = "/") => {
        try {
            // Show loading toast
            const googleLoginPromise = signInWithGooglePopup().then(async (googleResponse) => {

                // Decode the JWT to get user information
                const userInfo = decodeJWT(googleResponse.credential);
                //console.log('📧 Google User Info:', userInfo);

                // Get device information
                const deviceInfo = getDeviceInformation();

                // Call backend API with Google credentials and user details
                const referralCode = localStorage.getItem('referral_code') || '';
                const backendResponse = await dispatch(loginWithGoogle({
                    credential: googleResponse.credential,
                    clientId: googleResponse.clientId,
                    email: userInfo?.email,
                    name: userInfo?.name,
                    provider_id: userInfo?.sub, // Google's unique user ID
                    picture: userInfo?.picture, // Optional: profile picture URL
                    phone_number: '', // Will be added during profile completion if needed
                    device_information: deviceInfo,
                    referral_code: referralCode,
                })).unwrap();

                if (backendResponse.status === 0) {
                    throw new Error(backendResponse.message || "Google login failed.");
                }

                // Clear referral code after successful signup/login
                if (referralCode) {
                    localStorage.removeItem('referral_code');
                }

                // Login successful - handle local cart sync

                try {
                    // Sync local cart to user account
                    const cartSynced = await syncLocalCartToAccount(dispatch);

                    // Fetch updated cart info after sync
                    await dispatch(fetchCartInfo());

                    // Navigate to intended destination or home
                    navigate(from, { replace: true });

                    // Return appropriate success message
                    if (cartSynced) {
                        return backendResponse.message || "Login successful\! Your cart has been updated.";
                    } else {
                        return backendResponse.message || "Login successful\!";
                    }
                } catch (syncError) {
                    console.error("Cart sync error:", syncError);
                    // Still navigate and show success even if cart sync fails
                    navigate(from, { replace: true });
                    return backendResponse.message || "Login successful\!";
                }
            });
            

        } catch (error) {
            console.error("Google login error:", error);

            // Enhanced error handling for common OAuth issues
            let errorMessage = error.message || "Failed to sign in with Google. Please try again.";

            if (error.message && (error.message.includes('401') || error.message.includes('403') || error.message.includes('invalid_client'))) {
                errorMessage = "Google OAuth configuration error. Please check the console for debug information.";
            }

            toast.error(errorMessage);
        }
    };

    return { handleGoogleLogin };
};
